import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { categoryTooltip } from "../../../constants/TooltipInformation";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import {
  validateSpace,
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const Category = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [departmentList, setDepartmentList] = useState<string[]>([]);
  const [departmentName, setDepartmentName] = useState<string | null>(null);
  const [categoryInformation, setCategoryInformation] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const categoryPayload = {
    name: categoryName,
    departmentName: departmentName,
  };

  const addCategory = () => {
    if (validateInput()) {
      if (!categoryName || categoryName === null || categoryName === "") {
        setNameError(true);
      }
      if (!departmentName || departmentName === null || departmentName === "") {
        setAlertMessage("Enter the department name");
        setAlertType("error");
        setOpenAlert(true);
        return;
      } else if (
        checkDuplicate(
          Array.isArray(categoryInformation) ? categoryInformation : [],
          DNDFieldsEnum.CATEGORYCODE,
          categoryName,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setCategoryInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...categoryPayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...categoryPayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(categoryPayload);
        editCategory(null);
        setEditClicked(false);
      }
    }
  };

  const editCategory = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setCategoryName(list?.name);
    setDepartmentName(list?.departmentName);
  };

  const updateCategory = () => {
    if (!validateInput()) return;
    if (!categoryName || categoryName === null || categoryName === "") {
      setNameError(true);
      return;
    }
    if (!departmentName || departmentName === null || departmentName === "") {
      setAlertMessage("Enter the department name");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(categoryInformation) ? categoryInformation : []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.CATEGORYCODE,
        categoryName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setCategoryInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...categoryPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editCategory(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.category} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteCategory = (id: number) => {
    setCategoryInformation(deleteItem(categoryInformation, id));
  };

  const validateInput = () => {
    setNameError(false);
    if (
      categoryName &&
      !validateSpace(
        categoryName,
        validationLength.categoryName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    data(categoryInformation);
    editClicked &&
      setSelectedId(
        checkData(categoryInformation, selectedId) ? selectedId : null,
      );
  }, [categoryInformation]);

  useEffect(() => {
    if (fieldValues) {
      setCategoryInformation(
        Array.isArray(fieldValues[RoomPropertyEnum.category])
          ? fieldValues[RoomPropertyEnum.category]
          : fieldValues[RoomPropertyEnum.category]
            ? [fieldValues[RoomPropertyEnum.category]]
            : null,
      );
      setDepartmentList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.department],
          DNDFieldsEnum.DEPARTMENTCODE,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    validateInput();
  }, [categoryName]);

  const categoryNameHelperText = nameError
    ? !(categoryName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.category.categoryName
    : versaFieldHelperText.category.categoryName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Category Information
              <Tooltip title={tooltipComponent(categoryTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editCategory(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editCategory(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Category Name
                <Tooltip title={tooltipComponent(categoryTooltip.categoryName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="categoryName"
                variant="outlined"
                placeholder="Category Name"
                helperText={categoryNameHelperText}
                value={categoryName ?? ""}
                error={nameError}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setCategoryName(e.target.value.toUpperCase());
                  setNameError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Department Name
                <Tooltip
                  title={tooltipComponent(categoryTooltip.departmentName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                id="departmentName"
                disablePortal
                options={removeSampleTestArray(departmentList)}
                sx={{ width: "100%" }}
                value={departmentName ?? ""}
                disabled={!departmentList}
                onChange={(e: any) => setDepartmentName(e.target.innerText)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Department Name" />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateCategory : addCategory}
              id="addNewCategory"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Category
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Category Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {categoryInformation ? (
                <DraggableItemList
                  itemData={categoryInformation}
                  onOrderUpdate={(order) => setCategoryInformation(order)}
                  onEdit={(info) => {
                    editCategory(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteCategory(id)}
                  code={DNDFieldsEnum.CATEGORYCODE}
                  name={""}
                />
              ) : (
                <Typography variant="body2">
                  No Category Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Category;
